import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { fetchServiceBySlug, fetchGalleryBySlug } from '../lib/cms'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function ServiceDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [service, setService] = useState(null)
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeImageIdx, setActiveImageIdx] = useState(null)

  const metaTitle = service?.meta_titel || (service?.titel ? `${service.titel} | Terapien.dk - Birgitte Vilsgaard` : 'Ydelse | Terapien.dk')
  const metaDescription = service?.meta_beskrivelse || service?.kort_beskrivelse || 'Læs mere om vores ydelser og terapeutiske tilbud hos Terapien.dk i Svendborg.'
  const heroImg = Array.isArray(service?.hero_billede) ? service.hero_billede[0] : service?.hero_billede

  useDocumentMeta({
    title: metaTitle,
    description: metaDescription,
    image: heroImg
  })

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true)
      const serviceData = await fetchServiceBySlug(slug)
      if (!serviceData) {
        navigate('/404')
        return
      }
      setService(serviceData)

      if (serviceData.galleri_slug) {
        const galleryData = await fetchGalleryBySlug(serviceData.galleri_slug)
        setGallery(galleryData)
      }
      setLoading(false)
    }
    loadServiceData()
  }, [slug, navigate])

  if (loading) {
    return (
      <div className="pt-[72px] min-h-screen flex items-center justify-center bg-brand-offwhite">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-dark" />
      </div>
    )
  }

  const handlePrevImage = (e) => {
    e.stopPropagation()
    if (!gallery?.billeder) return
    setActiveImageIdx((prev) => (prev === 0 ? gallery.billeder.length - 1 : prev - 1))
  }

  const handleNextImage = (e) => {
    e.stopPropagation()
    if (!gallery?.billeder) return
    setActiveImageIdx((prev) => (prev === gallery.billeder.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="pt-[72px] min-h-screen bg-brand-offwhite">
      {/* Hero Section */}
      <section className="relative bg-brand-dark text-brand-offwhite py-20 lg:py-28 overflow-hidden">
        {/* Background Image with Overlay */}
        {service.hero_billede?.[0] && (
          <div className="absolute inset-0 opacity-20">
            <img
              src={service.hero_billede[0]}
              alt={service.titel}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/80 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col gap-4">
          <Link
            to="/ydelser"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-sage hover:text-brand-sand transition-colors duration-150"
          >
            <i className="fa-light fa-arrow-left text-[10px]" /> Tilbage til Temaer
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-sand leading-tight">
            {service.titel}
          </h1>
          <p className="text-base sm:text-lg font-serif italic text-brand-offwhite/80 max-w-3xl leading-relaxed">
            {service.kort_beskrivelse}
          </p>
        </div>
      </section>

      {/* Main Content & Gallery */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left Column: Long Description */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            <div
              className="prose text-brand-gray/90 text-sm sm:text-base"
              dangerouslySetInnerHTML={{ __html: service.lang_beskrivelse }}
            />
          </div>

          {/* Right Column: Gallery */}
          {gallery?.billeder && gallery.billeder.length > 0 && (
            <div className="lg:col-span-4 flex flex-col gap-6">
              <div className="flex flex-col gap-2 border-b border-brand-sand/40 pb-4">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-sage font-semibold">Galleri</span>
                <h2 className="font-serif text-xl font-bold text-brand-dark">{gallery.titel}</h2>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {gallery.billeder.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIdx(idx)}
                    className="relative aspect-square rounded-xl overflow-hidden bg-brand-sand/20 border border-brand-sand/30 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 group"
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,height=300,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                      alt={`${gallery.titel} - ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/10 flex items-center justify-center transition-colors duration-200">
                      <i className="fa-light fa-magnifying-glass-plus text-brand-offwhite opacity-0 group-hover:opacity-100 text-lg transition-opacity duration-200" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Fullscreen Lightbox Modal */}
      {activeImageIdx !== null && gallery?.billeder && (
        <div
          onClick={() => setActiveImageIdx(null)}
          className="fixed inset-0 bg-brand-dark/95 z-50 flex items-center justify-center p-4 sm:p-8 animate-fadeIn cursor-zoom-out"
        >
          {/* Close Button */}
          <button
            onClick={() => setActiveImageIdx(null)}
            className="absolute top-6 right-6 text-brand-offwhite hover:text-brand-sand text-2xl p-2 transition-colors duration-150"
            aria-label="Luk galleri"
          >
            <i className="fa-light fa-xmark" />
          </button>

          {/* Prev Button */}
          <button
            onClick={handlePrevImage}
            className="absolute left-6 text-brand-offwhite hover:text-brand-sand text-3xl p-4 transition-colors duration-150 hidden sm:block"
            aria-label="Forrige billede"
          >
            <i className="fa-light fa-chevron-left" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-5xl max-h-[80vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={gallery.billeder[activeImageIdx]}
              alt={`${gallery.titel} - ${activeImageIdx + 1}`}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl select-none"
            />
            <span className="text-brand-sand text-xs uppercase tracking-wider font-mono">
              {activeImageIdx + 1} / {gallery.billeder.length}
            </span>
          </div>

          {/* Next Button */}
          <button
            onClick={handleNextImage}
            className="absolute right-6 text-brand-offwhite hover:text-brand-sand text-3xl p-4 transition-colors duration-150 hidden sm:block"
            aria-label="Næste billede"
          >
            <i className="fa-light fa-chevron-right" />
          </button>
        </div>
      )}

      {/* Closing Contact CTA */}
      <section className="py-20 bg-brand-sand/20 border-t border-brand-sand/30 text-center">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-6">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
            {service.cta_tekst || 'Aftal en tid'}
          </h2>
          <p className="text-sm sm:text-base text-brand-gray/70 max-w-xl leading-relaxed">
            Kontakt mig i dag for at høre mere om, hvordan jeg kan hjælpe dig med {service.titel.toLowerCase()}.
          </p>
          <div className="mt-4 flex flex-wrap justify-center items-center gap-4">
            <Link
              to="/kontakt"
              className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              Book en samtale
            </Link>
            <Link
              to="/priser"
              className="bg-brand-offwhite border border-brand-dark/30 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Se priser
            </Link>
            <Link
              to="/kontakt"
              className="bg-brand-sand/30 border border-brand-sand/70 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
